// src/admin/pages/OneOffInvoicesPage.js
import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  RefreshCw, 
  Search,
  Eye,
  X,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Copy,
  ExternalLink,
  Loader2,
  Trash2
} from 'lucide-react';
import { supabase } from '../../config';
import { useNavigate } from 'react-router-dom';
import stripeService from '../../services/stripeService';
import { getUserDisplayName } from '../../services/utilityService';

const OneOffInvoicesPage = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editingFormData, setEditingFormData] = useState(null);
  const [finalizing, setFinalizing] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [sendEmail, setSendEmail] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'draft', 'open', 'paid', 'void'

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      // Fetch invoices from two sources:
      // 1. billing_history (finalized invoices)
      // 2. Stripe drafts API (draft invoices that might not be in billing_history yet)
      
      let billingHistoryInvoices = [];
      let draftInvoices = [];
      
      // Fetch from billing_history (finalized invoices)
      try {
        const joinResult = await supabase
          .from('billing_history')
          .select(`
            *,
            user_profiles (
              id,
              first_name,
              last_name,
              email,
              username
            )
          `)
          .is('subscription_id', null)
          .order('created_at', { ascending: false });
        
        if (joinResult.error) {
          const errorCode = joinResult.error.code || joinResult.error.message || '';
          const errorMessage = joinResult.error.message || '';
          
          if (errorCode === 'PGRST200' || errorMessage.includes('foreign key relationship')) {
            // Fetch without join
            const simpleResult = await supabase
              .from('billing_history')
              .select('*')
              .is('subscription_id', null)
              .order('created_at', { ascending: false });
            
            billingHistoryInvoices = simpleResult.data || [];
            
            // Fetch user profiles separately
            if (billingHistoryInvoices.length > 0) {
              const userIds = billingHistoryInvoices.map(inv => inv.user_id).filter(Boolean);
              if (userIds.length > 0) {
                const { data: profiles } = await supabase
                  .from('user_profiles')
                  .select('id, first_name, last_name, email, username')
                  .in('id', userIds);
                
                if (profiles) {
                  const profileMap = {};
                  profiles.forEach(profile => {
                    profileMap[profile.id] = profile;
                  });
                  billingHistoryInvoices = billingHistoryInvoices.map(inv => ({
                    ...inv,
                    user_profiles: profileMap[inv.user_id] || null
                  }));
                }
              }
            }
          } else {
            console.error('Error fetching billing history:', joinResult.error);
          }
        } else {
          billingHistoryInvoices = joinResult.data || [];
        }
      } catch (err) {
        console.error('Error fetching billing history:', err);
      }
      
      // Fetch draft invoices from Stripe API
      try {
        const response = await fetch('/api/stripe_api/invoices/drafts', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.invoices) {
            // Convert Stripe invoice format to billing_history format
            draftInvoices = data.invoices.map(inv => {
              // Check if this draft is already in billing_history
              const existing = billingHistoryInvoices.find(
                bh => bh.stripe_invoice_id === inv.id
              );
              
              // If it exists in billing_history, use that version (it has user info)
              if (existing) {
                return null; // Will be filtered out
              }
              
              // Convert Stripe invoice to billing_history format
              const customer = inv.customer;
              const customerName = typeof customer === 'object' ? customer.name : null;
              const customerEmail = typeof customer === 'object' ? customer.email : null;
              
              return {
                id: `draft_${inv.id}`, // Temporary ID
                stripe_invoice_id: inv.id,
                invoice_number: inv.number || inv.id,
                status: inv.status || 'draft',
                amount_cents: inv.total || inv.amount_due || 0,
                currency: inv.currency || 'usd',
                description: inv.description || '',
                date: inv.created ? new Date(inv.created * 1000).toISOString() : new Date().toISOString(),
                created_at: inv.created ? new Date(inv.created * 1000).toISOString() : new Date().toISOString(),
                hosted_invoice_url: inv.hosted_invoice_url,
                metadata: {
                  invoice_type: 'one_off',
                  line_items: inv.lines?.data || [],
                  customer_name: customerName,
                  customer_email: customerEmail
                },
                user_profiles: null, // Will try to match by customer_id
                customer_id: typeof customer === 'string' ? customer : customer?.id
              };
            }).filter(Boolean); // Remove nulls (duplicates)
            
            // Try to match draft invoices with user profiles by customer_id
            if (draftInvoices.length > 0) {
              const customerIds = draftInvoices.map(inv => inv.customer_id).filter(Boolean);
              if (customerIds.length > 0) {
                // Get user_ids from subscriptions table
                const { data: subs } = await supabase
                  .from('subscriptions')
                  .select('user_id, stripe_customer_id')
                  .in('stripe_customer_id', customerIds);
                
                if (subs) {
                  const customerToUserMap = {};
                  subs.forEach(sub => {
                    if (sub.stripe_customer_id) {
                      customerToUserMap[sub.stripe_customer_id] = sub.user_id;
                    }
                  });
                  
                  const userIds = Object.values(customerToUserMap).filter(Boolean);
                  if (userIds.length > 0) {
                    const { data: profiles } = await supabase
                      .from('user_profiles')
                      .select('id, first_name, last_name, email, username')
                      .in('id', userIds);
                    
                    if (profiles) {
                      const profileMap = {};
                      profiles.forEach(profile => {
                        profileMap[profile.id] = profile;
                      });
                      
                      draftInvoices = draftInvoices.map(inv => {
                        const userId = customerToUserMap[inv.customer_id];
                        return {
                          ...inv,
                          user_id: userId,
                          user_profiles: userId ? profileMap[userId] || null : null
                        };
                      });
                    }
                  }
                }
              }
            }
          }
        }
      } catch (err) {
        console.error('Error fetching draft invoices:', err);
      }
      
      // Merge both sources, removing duplicates (prefer billing_history version)
      const allInvoices = [...billingHistoryInvoices];
      const existingIds = new Set(billingHistoryInvoices.map(inv => inv.stripe_invoice_id));
      
      draftInvoices.forEach(draft => {
        if (!existingIds.has(draft.stripe_invoice_id)) {
          allInvoices.push(draft);
        }
      });
      
      // Sort by created_at descending
      allInvoices.sort((a, b) => {
        const dateA = new Date(a.created_at || a.date || 0);
        const dateB = new Date(b.created_at || b.date || 0);
        return dateB - dateA;
      });
      
      setInvoices(allInvoices);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (cents) => {
    if (!cents) return '$0.00';
    const dollars = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(dollars);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      const day = date.getDate();
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    } catch {
      return 'N/A';
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'paid': { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle },
      'open': { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock }, // Finalized but unpaid invoice
      'draft': { bg: 'bg-gray-100', text: 'text-gray-800', icon: Clock }, // Draft invoice
      'pending': { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock },
      'void': { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle }, // Voided invoice
      'uncollectible': { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle }, // Uncollectible invoice
      'failed': { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle },
      'refunded': { bg: 'bg-gray-100', text: 'text-gray-800', icon: XCircle }
    };

    const config = statusConfig[status?.toLowerCase()] || { bg: 'bg-gray-100', text: 'text-gray-800', icon: Clock };
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="h-3 w-3 mr-1" />
        {status}
      </span>
    );
  };

  const filteredInvoices = invoices.filter(invoice => {
    const searchLower = searchTerm.toLowerCase();
    const userDisplayName = getUserDisplayName(invoice.user_profiles || {}, '').toLowerCase();
    const matchesSearch = (
      invoice.invoice_number?.toLowerCase().includes(searchLower) ||
      invoice.description?.toLowerCase().includes(searchLower) ||
      userDisplayName.includes(searchLower) ||
      invoice.user_profiles?.email?.toLowerCase().includes(searchLower)
    );
    
    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const exportData = () => {
    const csvContent = [
      ['Invoice', 'Date', 'Customer', 'Email', 'Amount', 'Status', 'Description'].join(','),
      ...filteredInvoices.map(inv => [
        inv.invoice_number || 'N/A',
        formatDate(inv.date || inv.created_at),
        getUserDisplayName(inv.user_profiles || {}, 'N/A'),
        inv.user_profiles?.email || 'N/A',
        formatCurrency(inv.amount_cents),
        inv.status || 'N/A',
        (inv.description || 'N/A').replace(/,/g, ';')
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `one-off-invoices-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="w-full max-w-full">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading invoices...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">One-off Invoices</h1>
          <p className="text-gray-600 mt-2">Manage one-time invoices and payments</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => navigate('/adminhq/create-invoice')}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Invoice
          </button>
          <button
            onClick={exportData}
            className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </button>
          <button
            onClick={fetchInvoices}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Search Bar and Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex items-center gap-4">
          <div className="flex items-center flex-1">
            <Search className="h-5 w-5 text-gray-400 mr-3" />
            <input
              type="text"
              placeholder="Search invoices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 outline-none text-gray-700"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="text-gray-400 hover:text-gray-600 ml-2"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All</option>
              <option value="draft">Draft</option>
              <option value="open">Open</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="void">Void</option>
              <option value="uncollectible">Uncollectible</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900">
            Invoices ({filteredInvoices.length})
          </h3>
        </div>
        
        {filteredInvoices.length === 0 ? (
          <div className="p-8 text-center">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No invoices found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'Try adjusting your search' : 'No one-off invoices in the system yet'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto w-full max-w-full">
            <table className="w-full divide-y divide-gray-200" style={{ tableLayout: 'auto' }}>
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredInvoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {invoice.invoice_number || `INV-${invoice.id}`}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {getUserDisplayName(invoice.user_profiles || {}, invoice.metadata?.customer_name || 'Unknown User')}
                        </div>
                        <div className="text-sm text-gray-500">
                          {invoice.user_profiles?.email || invoice.metadata?.customer_email || 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(invoice.date || invoice.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(invoice.amount_cents || invoice.total || (invoice.metadata?.amount ? invoice.metadata.amount * 100 : 0))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(invoice.status)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {invoice.description || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      {invoice.status === 'draft' && (
                        <button
                          onClick={async () => {
                            // Fetch full invoice details for editing
                            try {
                              const invoiceId = invoice.stripe_invoice_id || invoice.id;
                              const invoiceDetails = await stripeService.getInvoice(invoiceId);
                              if (invoiceDetails.success && invoiceDetails.invoice) {
                                const inv = invoiceDetails.invoice;
                                setEditingFormData({
                                  description: inv.description || '',
                                  line_items: inv.line_items || inv.metadata?.line_items || [],
                                  days_until_due: 30,
                                  collection_method: 'send_invoice'
                                });
                                setEditingInvoice(invoice);
                              } else {
                                // Fallback to invoice data we have
                                setEditingFormData({
                                  description: invoice.description || '',
                                  line_items: invoice.metadata?.line_items || invoice.line_items || [],
                                  days_until_due: 30,
                                  collection_method: 'send_invoice'
                                });
                                setEditingInvoice(invoice);
                              }
                            } catch (err) {
                              console.error('Error fetching invoice details:', err);
                              // Fallback to invoice data we have
                              setEditingFormData({
                                description: invoice.description || '',
                                line_items: invoice.metadata?.line_items || invoice.line_items || [],
                                days_until_due: 30,
                                collection_method: 'send_invoice'
                              });
                              setEditingInvoice(invoice);
                            }
                          }}
                          className="text-green-600 hover:text-green-900"
                          title="Edit & Finalize"
                        >
                          <CheckCircle className="h-4 w-4 inline" />
                        </button>
                      )}
                      <button
                        onClick={() => setSelectedInvoice(invoice)}
                        className="text-blue-600 hover:text-blue-900"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4 inline" />
                      </button>
                      <button
                        onClick={() => setInvoiceToDelete(invoice)}
                        className="text-red-600 hover:text-red-900"
                        title={invoice.status === 'draft' ? 'Delete Invoice' : 'Void Invoice'}
                        disabled={deleting}
                      >
                        <Trash2 className="h-4 w-4 inline" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Invoice Details</h3>
                <button
                  onClick={() => setSelectedInvoice(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="max-h-96 overflow-auto">
                {/* Formatted Invoice Preview */}
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Invoice Number</p>
                      <p className="text-sm text-gray-900">{selectedInvoice.invoice_number || `INV-${selectedInvoice.id}`}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Status</p>
                      <p className="text-sm text-gray-900">{getStatusBadge(selectedInvoice.status || 'pending')}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Customer Name</p>
                      <p className="text-sm text-gray-900">{getUserDisplayName(selectedInvoice.user_profiles || {}, 'N/A')}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Customer Email</p>
                      <p className="text-sm text-gray-900">{selectedInvoice.user_profiles?.email || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Creation Date</p>
                      <p className="text-sm text-gray-900">{formatDate(selectedInvoice.date || selectedInvoice.created_at)}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Amount</p>
                      <p className="text-sm text-gray-900 font-semibold">
                        {formatCurrency(selectedInvoice.amount_cents || selectedInvoice.total || (selectedInvoice.metadata?.amount ? selectedInvoice.metadata.amount * 100 : 0))}
                      </p>
                    </div>
                  </div>
                  {selectedInvoice.description && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">Description</p>
                      <p className="text-sm text-gray-900">{selectedInvoice.description}</p>
                    </div>
                  )}
                  {((selectedInvoice.metadata?.line_items && selectedInvoice.metadata.line_items.length > 0) || (selectedInvoice.line_items && selectedInvoice.line_items.length > 0)) && (
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-2">Line Items</p>
                      <div className="border border-gray-200 rounded-md overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Product</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Qty</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Unit Price</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Total</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {(selectedInvoice.line_items || selectedInvoice.metadata?.line_items || []).map((item, idx) => {
                              const quantity = item.quantity || 1;
                              const unitAmount = item.unit_amount || (item.amount ? item.amount / quantity : 0);
                              const total = unitAmount * quantity;
                              return (
                                <tr key={idx}>
                                  <td className="px-3 py-2 text-gray-900">{item.description || 'N/A'}</td>
                                  <td className="px-3 py-2 text-right text-gray-900">{quantity}</td>
                                  <td className="px-3 py-2 text-right text-gray-900">
                                    ${(unitAmount / 100).toFixed(2)}
                                  </td>
                                  <td className="px-3 py-2 text-right text-gray-900 font-medium">
                                    ${(total / 100).toFixed(2)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {(!selectedInvoice.metadata?.line_items || selectedInvoice.metadata.line_items.length === 0) && (!selectedInvoice.line_items || selectedInvoice.line_items.length === 0) && (
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-2">Line Items</p>
                      <p className="text-sm text-gray-400">No line items available.</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-1">Currency</p>
                    <p className="text-sm text-gray-900 uppercase">{selectedInvoice.currency || selectedInvoice.metadata?.currency || 'USD'}</p>
                  </div>
                  {selectedInvoice.hosted_invoice_url && (
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-2">Invoice URL</p>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          readOnly
                          value={selectedInvoice.hosted_invoice_url}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-50"
                          onClick={(e) => e.target.select()}
                        />
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(selectedInvoice.hosted_invoice_url);
                          }}
                          className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm flex items-center"
                          title="Copy URL"
                        >
                          <Copy className="h-4 w-4 mr-1" />
                          Copy
                        </button>
                        <a
                          href={selectedInvoice.hosted_invoice_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm flex items-center"
                          title="Open in new tab"
                        >
                          <ExternalLink className="h-4 w-4 mr-1" />
                          Open
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setSelectedInvoice(null)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit/Finalize Modal */}
      {editingInvoice && editingFormData && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Edit & Finalize Invoice</h3>
                <button
                  onClick={() => {
                    setEditingInvoice(null);
                    setEditingFormData(null);
                    setSendEmail(false);
                    setMessage('');
                    setError('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                  {error}
                </div>
              )}

              {message && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm">
                  {message}
                </div>
              )}

              <div className="space-y-6">
                {/* Invoice Info (Read-only) */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-md">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Invoice ID</p>
                    <p className="text-sm font-medium text-gray-900">{editingInvoice.stripe_invoice_id || editingInvoice.id}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Customer</p>
                    <p className="text-sm font-medium text-gray-900">
                      {getUserDisplayName(editingInvoice.user_profiles || {}, editingInvoice.metadata?.customer_name || 'N/A')}
                    </p>
                    <p className="text-xs text-gray-500">
                      {editingInvoice.user_profiles?.email || editingInvoice.metadata?.customer_email || 'N/A'}
                    </p>
                  </div>
                </div>

                {/* Editable Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={editingFormData.description}
                    onChange={(e) => setEditingFormData({...editingFormData, description: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Invoice description..."
                  />
                </div>

                {/* Editable Line Items */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Line Items
                  </label>
                  <div className="space-y-3">
                    {editingFormData.line_items.map((item, index) => (
                      <div key={index} className="flex gap-3 p-3 border border-gray-200 rounded-md">
                        <div className="flex-1">
                          <input
                            type="text"
                            value={item.description || ''}
                            onChange={(e) => {
                              const newItems = [...editingFormData.line_items];
                              newItems[index] = {...newItems[index], description: e.target.value};
                              setEditingFormData({...editingFormData, line_items: newItems});
                            }}
                            placeholder="Item description"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                        <div className="w-24">
                          <input
                            type="number"
                            value={item.quantity || 1}
                            onChange={(e) => {
                              const newItems = [...editingFormData.line_items];
                              newItems[index] = {...newItems[index], quantity: parseInt(e.target.value) || 1};
                              setEditingFormData({...editingFormData, line_items: newItems});
                            }}
                            min="1"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                            placeholder="Qty"
                          />
                        </div>
                        <div className="w-32">
                          <input
                            type="number"
                            value={item.unit_amount ? (item.unit_amount / 100).toFixed(2) : ''}
                            onChange={(e) => {
                              const newItems = [...editingFormData.line_items];
                              const unitAmount = Math.round(parseFloat(e.target.value || 0) * 100);
                              newItems[index] = {...newItems[index], unit_amount: unitAmount};
                              setEditingFormData({...editingFormData, line_items: newItems});
                            }}
                            step="0.01"
                            min="0"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                            placeholder="Unit Price"
                          />
                        </div>
                        <div className="w-24 flex items-center text-sm font-medium text-gray-700">
                          ${((item.unit_amount || 0) * (item.quantity || 1) / 100).toFixed(2)}
                        </div>
                        <button
                          onClick={() => {
                            const newItems = editingFormData.line_items.filter((_, i) => i !== index);
                            setEditingFormData({...editingFormData, line_items: newItems});
                          }}
                          className="px-2 py-1 text-red-600 hover:text-red-800"
                          title="Remove item"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => {
                        setEditingFormData({
                          ...editingFormData,
                          line_items: [...editingFormData.line_items, {description: '', quantity: 1, unit_amount: 0}]
                        });
                      }}
                      className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-md text-sm text-gray-600 hover:border-gray-400 hover:text-gray-700"
                    >
                      + Add Line Item
                    </button>
                  </div>
                  <div className="mt-3 p-3 bg-gray-50 rounded-md">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Total:</span>
                      <span className="text-lg font-bold text-gray-900">
                        ${(editingFormData.line_items.reduce((sum, item) => sum + ((item.unit_amount || 0) * (item.quantity || 1)), 0) / 100).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Email Option */}
                <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-md">
                  <input
                    type="checkbox"
                    id="sendEmail"
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="sendEmail" className="text-sm text-gray-700">
                    Send invoice to customer via email when finalizing
                  </label>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setEditingInvoice(null);
                    setEditingFormData(null);
                    setSendEmail(false);
                    setMessage('');
                    setError('');
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                  disabled={finalizing || updating}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateInvoice}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  disabled={finalizing || updating}
                >
                  {updating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <FileText className="h-4 w-4 mr-2" />
                      Update Invoice
                    </>
                  )}
                </button>
                <button
                  onClick={handleFinalizeInvoice}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  disabled={finalizing || updating || !editingFormData || !editingFormData.line_items || editingFormData.line_items.length === 0 || editingFormData.line_items.filter(item => item.description && item.description.trim() && item.unit_amount && item.unit_amount > 0).length === 0}
                  title={(!editingFormData || !editingFormData.line_items || editingFormData.line_items.length === 0 || editingFormData.line_items.filter(item => item.description && item.description.trim() && item.unit_amount && item.unit_amount > 0).length === 0) ? 'Add line items before finalizing' : 'Finalize invoice'}
                >
                  {finalizing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Finalizing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Finalize Invoice
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {invoiceToDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {invoiceToDelete.status === 'draft' ? 'Delete Invoice' : 'Void Invoice'}
                </h3>
                <button
                  onClick={() => setInvoiceToDelete(null)}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={deleting}
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-700">
                  Are you sure you want to {invoiceToDelete.status === 'draft' ? 'delete' : 'void'} this invoice?
                </p>
                <div className="mt-3 p-3 bg-gray-50 rounded-md">
                  <p className="text-xs text-gray-500 mb-1">Invoice</p>
                  <p className="text-sm font-medium text-gray-900">
                    {invoiceToDelete.invoice_number || `INV-${invoiceToDelete.id}`}
                  </p>
                  <p className="text-xs text-gray-500 mt-2 mb-1">Customer</p>
                  <p className="text-sm text-gray-900">
                    {getUserDisplayName(invoiceToDelete.user_profiles || {}, invoiceToDelete.metadata?.customer_name || 'Unknown')}
                  </p>
                  <p className="text-xs text-gray-500 mt-2 mb-1">Amount</p>
                  <p className="text-sm font-medium text-gray-900">
                    {formatCurrency(invoiceToDelete.amount_cents || invoiceToDelete.total || 0)}
                  </p>
                </div>
                {invoiceToDelete.status !== 'draft' && (
                  <p className="mt-3 text-xs text-yellow-600 bg-yellow-50 p-2 rounded">
                    Note: This will void the invoice. It cannot be undone and the invoice will be marked as voided.
                  </p>
                )}
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                  {error}
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setInvoiceToDelete(null);
                    setError('');
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteInvoice}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  disabled={deleting}
                >
                  {deleting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {invoiceToDelete.status === 'draft' ? 'Deleting...' : 'Voiding...'}
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      {invoiceToDelete.status === 'draft' ? 'Delete' : 'Void'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  async function handleUpdateInvoice() {
    if (!editingInvoice || !editingFormData) return;
    
    setUpdating(true);
    setError('');
    setMessage('');

    try {
      const invoiceId = editingInvoice.stripe_invoice_id || editingInvoice.id;
      
      // Prepare update data
      const updateData = {
        description: editingFormData.description,
        line_items: editingFormData.line_items.map(item => ({
          description: item.description,
          quantity: item.quantity || 1,
          unit_amount: item.unit_amount || 0,
          currency: item.currency || 'usd'
        }))
      };
      
      // Update the invoice
      const updateResponse = await stripeService.updateInvoice(invoiceId, updateData);
      
      if (!updateResponse.success) {
        throw new Error(updateResponse.error || 'Failed to update invoice');
      }

      setMessage('Invoice updated successfully!');
      
      // Refresh invoice list and reload invoice details
      setTimeout(async () => {
        await fetchInvoices();
        // Reload invoice details for editing
        try {
          const invoiceDetails = await stripeService.getInvoice(invoiceId);
          if (invoiceDetails.success && invoiceDetails.invoice) {
            const inv = invoiceDetails.invoice;
            setEditingFormData({
              description: inv.description || '',
              line_items: inv.line_items || [],
              days_until_due: 30,
              collection_method: 'send_invoice'
            });
          }
        } catch (err) {
          console.error('Error reloading invoice:', err);
        }
        setMessage('');
      }, 1500);

    } catch (err) {
      console.error('Error updating invoice:', err);
      setError(err.message || 'Failed to update invoice');
    } finally {
      setUpdating(false);
    }
  }

  async function handleFinalizeInvoice() {
    if (!editingInvoice) return;
    
    setFinalizing(true);
    setError('');
    setMessage('');

    try {
      const invoiceId = editingInvoice.stripe_invoice_id || editingInvoice.id;
      
      // Validate that invoice has line items before finalizing
      if (!editingFormData || !editingFormData.line_items || editingFormData.line_items.length === 0) {
        setError('Cannot finalize invoice: Please add at least one line item before finalizing.');
        setFinalizing(false);
        return;
      }
      
      // Filter out empty line items (no description or zero amount)
      const validLineItems = editingFormData.line_items.filter(item => 
        item.description && item.description.trim() && 
        item.unit_amount && item.unit_amount > 0
      );
      
      if (validLineItems.length === 0) {
        setError('Cannot finalize invoice: Please add at least one line item with a description and amount before finalizing.');
        setFinalizing(false);
        return;
      }
      
      // If form data has been modified, update first
      if (editingFormData) {
        try {
          const updateData = {
            description: editingFormData.description,
            line_items: validLineItems.map(item => ({
              description: item.description,
              quantity: item.quantity || 1,
              unit_amount: item.unit_amount || 0,
              currency: item.currency || 'usd'
            }))
          };
          console.log('📝 Updating invoice before finalization:', updateData);
          const updateResponse = await stripeService.updateInvoice(invoiceId, updateData);
          console.log('✅ Invoice updated, waiting for Stripe to process...', updateResponse);
          
          // Wait a moment for Stripe to process the line items and recalculate totals
          await new Promise(resolve => setTimeout(resolve, 2000));
        } catch (updateErr) {
          console.warn('Failed to update before finalizing:', updateErr);
          // Continue with finalization anyway - maybe the invoice is already updated
        }
      }
      
      // Finalize the invoice
      console.log('🎯 Finalizing invoice:', invoiceId);
      const finalizeResponse = await stripeService.finalizeInvoice(invoiceId, { send_invoice: sendEmail });
      
      if (!finalizeResponse.success) {
        throw new Error(finalizeResponse.error || 'Failed to finalize invoice');
      }

      setMessage('Invoice finalized successfully! ' + (sendEmail ? 'Email sent to customer.' : ''));
      setSendEmail(false);
      
      // Close modal first
      setEditingInvoice(null);
      setEditingFormData(null);
      
      // Refresh invoice list - wait a bit longer for database to update
      setTimeout(async () => {
        await fetchInvoices();
        setMessage('');
      }, 3000);

    } catch (err) {
      console.error('Error finalizing invoice:', err);
      setError(err.message || 'Failed to finalize invoice');
    } finally {
      setFinalizing(false);
    }
  }

  async function handleDeleteInvoice() {
    if (!invoiceToDelete) return;
    
    setDeleting(true);
    setError('');
    setMessage('');

    try {
      const invoiceId = invoiceToDelete.stripe_invoice_id || invoiceToDelete.id;
      
      const deleteResponse = await stripeService.deleteInvoice(invoiceId);
      
      if (!deleteResponse.success) {
        throw new Error(deleteResponse.error || 'Failed to delete invoice');
      }

      setMessage(deleteResponse.message || `Invoice ${deleteResponse.action || 'deleted'} successfully!`);
      
      // Close modal first
      setInvoiceToDelete(null);
      
      // Refresh invoice list
      setTimeout(async () => {
        await fetchInvoices();
        setMessage('');
      }, 1000);

    } catch (err) {
      console.error('Error deleting invoice:', err);
      setError(err.message || 'Failed to delete invoice');
    } finally {
      setDeleting(false);
    }
  }
};

export default OneOffInvoicesPage;

